import { CSSProperties, ReactElement, useCallback } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  DropResult,
  Droppable,
  NotDraggingStyle,
  OnDragEndResponder,
} from 'react-beautiful-dnd';

import './draggable-list.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface DraggableListPropsChildProps<T = any> {
  item: T;
  index: number;
}

type GetItemStyleFn = (
  style: CSSProperties,
  isDragging: boolean
) => CSSProperties;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface DraggableListProps<T = any> {
  droppableId: string;
  onDragEnd: OnDragEndResponder;
  items: T[];
  getItemId: (item: T) => string;
  disabled?: boolean;
  getItemStyle?: GetItemStyleFn;
  children: (props: DraggableListPropsChildProps) => ReactElement;
}

const DraggableList = (props: DraggableListProps) => {
  const {
    children,
    onDragEnd,
    disabled,
    droppableId,
    items,
    getItemId,
    getItemStyle,
  } = props;

  const getItemStyleInternal = useCallback(
    (
      style: DraggingStyle | NotDraggingStyle | undefined,
      isDragging: boolean
    ) => {
      const overrideProps: CSSProperties = {
        opacity: isDragging ? 0.8 : 1,
        userSelect: 'none',
        top: 'auto',
        left: 'auto',
      };

      let mergedStyle = {
        ...style,
        ...overrideProps,
      };

      if (typeof getItemStyle === 'function') {
        mergedStyle = getItemStyle(mergedStyle, isDragging);
      }

      return mergedStyle;
    },
    [getItemStyle]
  );

  const onDragEndInternal = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result: DropResult, provided: any) => {
      const from = result.source?.index;
      const to = result.destination?.index;

      if (from === to) {
        return;
      }

      if (typeof onDragEnd === 'function') {
        onDragEnd(result, provided);
      }
    },
    [onDragEnd]
  );

  return (
    <DragDropContext onDragEnd={onDragEndInternal}>
      <Droppable droppableId={droppableId}>
        {(droppableProvided) => (
          <div
            {...droppableProvided.droppableProps}
            ref={droppableProvided.innerRef}
          >
            {items.map((item, index) => {
              const itemId = getItemId(item);
              return (
                <Draggable
                  key={itemId}
                  draggableId={itemId}
                  index={index}
                  isDragDisabled={disabled}
                >
                  {(draggableProvided, contentState) => {
                    return (
                      <div
                        className="draggable-item-spacing"
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                        style={getItemStyleInternal(
                          draggableProvided.draggableProps.style,
                          contentState.isDragging
                        )}
                      >
                        {children({ item, index })}
                      </div>
                    );
                  }}
                </Draggable>
              );
            })}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export { DraggableList };
